import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiCloudRain, FiCalendar, FiMapPin, FiAlertTriangle, FiCopy } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getTripForecastById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminTripForecastDetail = () => {
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchForecast = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getTripForecastById(forecastId);
                // API returns { forecast_details: { ... } }
                setForecastData(data?.forecast_details || data || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement de la prévision'));
            } finally {
                setLoading(false);
            }
        };
        // Always fetch by id to ensure up-to-date data
        if (forecastId) fetchForecast();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forecastId]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision de séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de la prévision...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision de séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!forecastData) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision de séjour</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de prévision disponible pour l'identifiant: {forecastId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    const copyWeatherDataToClipboard = async () => {
        if (!weatherData || weatherData.length === 0) return;

        // Créer le contenu CSV
        const headers = ['Date', 'Heure', 'Nombre de mm de précipitations'];
        const rows = weatherData.map(data => {
            const date = `${data.forecast_day}/${data.forecast_month}/${data.forecast_year}`;
            return [date, data.forecast_hour || '', data.value || '0,0'];
        });

        const csvContent = [
            headers.join('\t'),
            ...rows.map(row => row.join('\t'))
        ].join('\n');

        try {
            await navigator.clipboard.writeText(csvContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Fallback pour les navigateurs plus anciens
            const textArea = document.createElement('textarea');
            textArea.value = csvContent;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (fallbackErr) {
                console.error('Erreur lors de la copie:', fallbackErr);
                alert('Erreur lors de la copie des données');
            }
            document.body.removeChild(textArea);
        }
    };

    const copyWeatherTempToClipboard = async () => {
        if (!weatherTemp || weatherTemp.length === 0) return;

        const headers = ['Date', 'Heure', 'Température (°C)'];
        const rows = weatherTemp.map(data => {
            const date = `${data.forecast_day}/${data.forecast_month}/${data.forecast_year}`;
            return [date, data.forecast_hour || '', data.value || 'N/A'];
        });

        const csvContent = [
            headers.join('\t'),
            ...rows.map(row => row.join('\t'))
        ].join('\n');

        try {
            await navigator.clipboard.writeText(csvContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = csvContent;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (fallbackErr) {
                console.error('Erreur lors de la copie:', fallbackErr);
                alert('Erreur lors de la copie des données de température');
            }
            document.body.removeChild(textArea);
        }
    };

    const forecastInfo = forecastData.forecast_info || {};
    const policyInfo = forecastData.policy_info || {};
    const activityInfo = forecastData.activity_info || {};
    const clientInfo = forecastData.client_info || {};
    const partnerInfo = forecastData.partner_info || {};
    const referenceInfo = forecastData.reference_info || {};
    const weatherData = forecastInfo.weather_data || [];
    const weatherTemp = forecastInfo.weather_temp || [];
    const weatherWind = forecastInfo.weather_wind || [];
    const subcoverageTrips = referenceInfo?.subcoverage_trips || [];
    const firstSub = Array.isArray(subcoverageTrips) && subcoverageTrips.length > 0 ? subcoverageTrips[0] : {};
    const formatWeatherParameter = (value) => {
        if (value == null || value === '') return 'N/A';
        if (Array.isArray(value)) return value.filter(Boolean).join(', ') || 'N/A';
        return String(value);
    };

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiCloudRain className="me-2" />
                    Détail de la prévision de séjour
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {/* Informations prévision */}
            <Section title="Informations prévision">
                <Row className="mb-2">
                    <Col md={4}><Label>Jour du séjour</Label><Value><FiCalendar className="me-1" />{get(forecastInfo, ['trip_day'])}</Value></Col>
                    <Col md={4}><Label>Source de données</Label><Value>{get(forecastInfo, ['data_source'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Coordonnées</Label><Value><FiMapPin className="me-1" />{get(forecastInfo, ['latitude'])}, {get(forecastInfo, ['longitude'])}</Value></Col>
                    <Col md={4}><Label>Adresse</Label><Value>{get(forecastInfo, ['address'])}</Value></Col>
                    <Col md={4}><Label>Total mm précipitation</Label><Value>{get(forecastInfo, ['total_mm_prc'])} mm</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Année d'initialisation</Label><Value>{get(forecastInfo, ['meteomatics_init_year'])}</Value></Col>
                    <Col md={4}><Label>Mois d'initialisation</Label><Value>{get(forecastInfo, ['meteomatics_init_month'])}</Value></Col>
                    <Col md={4}><Label>Jour d'initialisation</Label><Value>{get(forecastInfo, ['meteomatics_init_day'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Heure UTC</Label><Value>{get(forecastInfo, ['meteomatics_run_hour_utc'])}</Value></Col>
                    <Col md={4}><Label>Heure Paris</Label><Value>{get(forecastInfo, ['meteomatics_run_hour_paris'])}</Value></Col>
                    <Col md={4}><Label>Dernière modification</Label><Value>{get(forecastInfo, ['forecast_last_modified_at'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Modifié par</Label><Value>{get(forecastInfo, ['forecast_last_modified_by'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations police */}
            <Section title="Informations police">
                <Row className="mb-2">
                    <Col md={4}><Label>Code police</Label><Value>{get(policyInfo, ['policy_code'])}</Value></Col>
                    <Col md={4}><Label>Numéro de réservation</Label><Value>{get(activityInfo, ['reservation_number'])}</Value></Col>
                </Row>
                <PolicySumInsuredRows source={policyInfo} Label={Label} Value={Value} colMd={6} />
            </Section>

            {/* Informations activité */}
            <Section title="Informations activité">
                <Row className="mb-2">
                    <Col md={4}><Label>Code activité</Label><Value>{get(activityInfo, ['activity_code'])}</Value></Col>
                    <Col md={4}><Label>Nom</Label><Value>{get(activityInfo, ['name'])}</Value></Col>
                    <Col md={4}><Label>Catégorie</Label><Value>{get(activityInfo, ['category'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date début activité</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['activity_start_date'])}</Value></Col>
                    <Col md={4}><Label>Heure début</Label><Value>{get(activityInfo, ['activity_start_time'])}</Value></Col>
                    <Col md={4}><Label>Date fin activité</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['activity_end_date'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Heure fin</Label><Value>{get(activityInfo, ['activity_end_time'])}</Value></Col>
                    <Col md={4}><Label>Durée</Label><Value>{get(activityInfo, ['duration'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date début effective</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['trip_effective_start_date'])}</Value></Col>
                    <Col md={4}><Label>Date fin effective</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['trip_effective_end_date'])}</Value></Col>
                    <Col md={4}><Label>Date modifiée</Label><Value><Badge bg={get(activityInfo, ['trip_modified']) ? 'warning' : 'secondary'}>{get(activityInfo, ['trip_modified']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Adresse</Label><Value>{get(activityInfo, ['activity_address'])}</Value></Col>
                    <Col md={4}><Label>Ville</Label><Value>{get(activityInfo, ['activity_city'])}</Value></Col>
                    <Col md={4}><Label>Code postal</Label><Value>{get(activityInfo, ['activity_zip_code'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Pays</Label><Value>{get(activityInfo, ['activity_country'])}</Value></Col>
                    <Col md={4}><Label>Prix</Label><Value>{get(activityInfo, ['activity_price'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Fournisseur</Label><Value>{get(activityInfo, ['supplier_name'])}</Value></Col>
                    <Col md={4}><Label>Adresse fournisseur</Label><Value>{get(activityInfo, ['supplier_address'])}</Value></Col>
                    <Col md={4}><Label>Ville fournisseur</Label><Value>{get(activityInfo, ['supplier_city'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations client */}
            <Section title="Informations client">
                <Row className="mb-2">
                    <Col md={4}><Label>Prénom</Label><Value>{get(clientInfo, ['first_name'])}</Value></Col>
                    <Col md={4}><Label>Nom</Label><Value>{get(clientInfo, ['last_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(clientInfo, ['email'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations partenaire */}
            <Section title="Informations partenaire">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom de l'entreprise</Label><Value>{get(partnerInfo, ['company_name'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations référence */}
            {referenceInfo && Object.keys(referenceInfo).length > 0 && (
                <Section title="Informations référence">
                    <Row className="mb-2">
                        <Col md={4}><Label>Nom référence</Label><Value>{get(referenceInfo, ['reference_name'])}</Value></Col>
                        <Col md={4}><Label>Paramètre météo</Label><Value>{formatWeatherParameter(firstSub?.weather_parameter ?? referenceInfo.weather_parameter)}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Heure déclencheur (précipitation)</Label><Value>{get(firstSub, ['prcp_trigger_hour_inf'])}</Value></Col>
                        <Col md={4}><Label>Nombre d'heures (précipitation)</Label><Value>{get(firstSub, ['trigger_number_hours'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Heure déclencheur (température)</Label><Value>{get(firstSub, ['temperature_trigger_hour_inf'], get(firstSub, ['temperature_trigger_hour_inf']))}</Value></Col>
                        <Col md={4}><Label>Nombre d'heures (température)</Label><Value>{get(firstSub, ['temperature_trigger_number_hours'])}</Value></Col>
                    </Row>
                </Section>
            )}

            {/* Données météorologiques */}
            <WeatherDataSections
                weatherData={weatherData}
                weatherTemp={weatherTemp}
                weatherWind={weatherWind}
                onCopyPrecipitation={copyWeatherDataToClipboard}
                onCopyTemperature={copyWeatherTempToClipboard}
                copied={copied}
                idPrefix="trip-forecast"
            />
        </div>
    );
};

export default AdminTripForecastDetail;
